// What a work card carries besides its steps: the SOPs of its operations and the checklist
// lines of its operations and items, pulled together fresh each time the card is opened.
// Nothing is written per card except the ticks.

import { useMemo } from 'react';
import { Rat, assembleCard, cardCanClose, type AssembledCard, type ChecklistLine, type Sop, type Value } from '@zerogap/engine';
import { deleteRow, fetchRows, insertRow, useRows, type Row } from './api';
import { useJobData, useResolved } from './job';

export interface CardRefs {
  loading: boolean;
  assembled: AssembledCard;
  /** SOPs by the operation they belong to, for showing beside a step. */
  sopsFor: (operationId: string | null | undefined) => Row[];
  ticked: Set<string>;
  ticks: Row[];
  canClose: { ok: boolean; missing: ChecklistLine[] };
}

const asValue = (v: unknown): Value => {
  if (v === null || v === undefined) return null;
  if (typeof v === 'boolean') return v;
  return Rat.tryParse(String(v)) ?? String(v);
};

const toLine = (r: Row): ChecklistLine => ({
  id: r.id, operation_id: r.operation_id ?? null, catalog_item_id: r.catalog_item_id ?? null, text: r.text, required: !!r.required, show_when: r.show_when_expr ?? null, sort: r.sort ?? 0,
});
const toSop = (r: Row): Sop => ({ id: r.id, operation_id: r.operation_id, title: r.title, url: r.url ?? null, sort: r.sort ?? 0 });

export function useCardReferences(card: Row | null | undefined, steps: Row[]): CardRefs {
  const data = useJobData(card?.job_id);
  const r = useResolved(data);
  const opIds = useMemo(() => [...new Set(steps.map((x) => x.operation_id as string | null).filter(Boolean) as string[])], [steps]);
  const itemIds = useMemo(() => ((card?.items as string[]) ?? []), [card?.items]);
  const mine = useMemo(() => (r.res?.items ?? []).filter((i) => itemIds.includes(i.id)), [r.res, itemIds]);
  const catIds = useMemo(() => [...new Set(mine.map((i) => i.catalog_item_id).filter(Boolean) as string[])], [mine]);

  const sops = useRows('sop', { in: { operation_id: opIds }, order: ['sort'] });
  const opLines = useRows('checklist_line', { in: { operation_id: opIds }, order: ['sort'] });
  const itemLines = useRows('checklist_line', { in: { catalog_item_id: catIds }, order: ['sort'] });
  const ticks = useRows('work_card_check', { eq: { work_card_id: card?.id }, enabled: !!card });

  return useMemo(() => {
    const assembled = assembleCard(
      {
        steps: steps.map((x) => ({ operation_id: (x.operation_id as string | null) ?? null })),
        items: mine.map((i) => ({
          id: i.id,
          number: i.number,
          catalog_item_id: i.catalog_item_id,
          values: Object.fromEntries([...i.inputs.map((x) => [x.key, asValue(x.value)] as const), ...i.derived.map((d) => [d.key, asValue(d.value)] as const)]),
        })),
      },
      sops.rows.map(toSop),
      [...opLines.rows, ...itemLines.rows].map(toLine),
    );
    const ticked = new Set(ticks.rows.map((t) => t.checklist_line_id as string));
    return {
      loading: sops.loading || opLines.loading || itemLines.loading || ticks.loading || (catIds.length === 0 && itemIds.length > 0 && r.loading),
      assembled,
      sopsFor: (operationId) => sops.rows.filter((x) => x.operation_id === operationId),
      ticked,
      ticks: ticks.rows,
      canClose: cardCanClose(assembled.checklist, ticked),
    };
  }, [steps, mine, sops.rows, sops.loading, opLines.rows, opLines.loading, itemLines.rows, itemLines.loading, ticks.rows, ticks.loading, catIds.length, itemIds.length, r.loading]);
}

export async function setTick(cardId: string, lineId: string, on: boolean, userId: string | null, ticks: Row[]): Promise<void> {
  const have = ticks.find((t) => t.checklist_line_id === lineId);
  if (on && !have) await insertRow('work_card_check', { work_card_id: cardId, checklist_line_id: lineId, ticked_by: userId });
  if (!on && have) await deleteRow('work_card_check', have.id);
}

/**
 * The same question asked without the screen's state, for the places a card is closed by
 * moving it on the board: which must-tick lines of this card are still unticked?
 */
export async function unticked(card: Row, steps: Row[], resolvedItems: { id: string; number: string; catalog_item_id: string | null; inputs: { key: string; value: unknown }[]; derived: { key: string; value: string }[] }[]): Promise<ChecklistLine[]> {
  const opIds = [...new Set(steps.map((x) => x.operation_id as string | null).filter(Boolean) as string[])];
  const itemIds = (card.items as string[]) ?? [];
  const mine = resolvedItems.filter((i) => itemIds.includes(i.id));
  const catIds = [...new Set(mine.map((i) => i.catalog_item_id).filter(Boolean) as string[])];
  const lines = [
    ...(opIds.length ? await fetchRows('checklist_line', { in: { operation_id: opIds } }) : []),
    ...(catIds.length ? await fetchRows('checklist_line', { in: { catalog_item_id: catIds } }) : []),
  ];
  if (!lines.some((l) => l.required)) return [];
  const ticks = await fetchRows('work_card_check', { eq: { work_card_id: card.id } });
  const assembled = assembleCard(
    {
      steps: steps.map((x) => ({ operation_id: (x.operation_id as string | null) ?? null })),
      items: mine.map((i) => ({ id: i.id, number: i.number, catalog_item_id: i.catalog_item_id, values: Object.fromEntries([...i.inputs.map((x) => [x.key, asValue(x.value)] as const), ...i.derived.map((d) => [d.key, asValue(d.value)] as const)]) })),
    },
    [],
    lines.map(toLine),
  );
  return cardCanClose(assembled.checklist, ticks.map((t) => t.checklist_line_id as string)).missing;
}
